import Components from "slotworks/components/components";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import Layers from "slotworks/graphics/layers/layers";
import RevolverInit from "slotworks/integration/revolver/states/revolver-init";
import SettingsModel, { Jurisdiction } from "slotworks/model/settings/settings-model";
import Translations from "slotworks/i18n/translations";
import betModel from "slotworks/model/bets/bet-model";
import RainbowStacksComponents from "components/components";
import currency from "slotworks/i18n/currency";
import settingsModel from "slotworks/model/settings/settings-model";
import EmptyLinesComponent from "components/lines/lines-component";
import SlotDefinition from "slotworks/model/slot-definition";
import rainbowStacksComponents from "components/components";
import NotificationPopup from "components/promo-popups/notification-popup";
import RSSpinReelTransition from "components/matrix/spin-reel-transition";
import RSSpineSymbolBehaviour from "components/matrix/spine-symbol-behaviour";
import LeprechaunSymbolBehaviour from "components/matrix/leprechaun-symbol-behaviour";
import deviceInfo from "slotworks/utils/device-info";
import canvasManager from "slotworks/graphics/canvas/canvas-manager";
import { sequence } from "slotworks/utils/race-utils";
import Contract from "slotworks/utils/contract";
import soundManager from "slotworks/sound/sound-manager";
import RSLandSpineSymbolBehaviour from "components/matrix/land-spine-symbol-behaviour";
import autoplayModel from "slotworks/model/autoplay-model";
import { GlowFilter } from "pixi-filters";

declare const __VERSION__: string;
export default class RainbowStacksInit extends RevolverInit {

    public onEnter() {
        RainbowStacksComponents.init();

        super.onEnter();
    }

    public onExit() {
        super.onExit();

        if(!settingsModel.isUKJurisdiction() && settingsModel.buyoutEngine) {   
            rainbowStacksComponents.bonusBuyButton.start();
        }
        
        rainbowStacksComponents.rulesButton.init();
        rainbowStacksComponents.rulesButton.setEnabled(true);

        rainbowStacksComponents.jackpotMeters.init();
    }

    public complete() {
        Components.matrix.stickyLayer = Layers.MatrixForeground;
        RainbowStacksComponents.scatterEmitter.init();
        Components.lines = new EmptyLinesComponent(SlotDefinition.lineDefinitions);

        Components.betBar.onBetUpdated.addOnce(() => {
            const notification = new NotificationPopup();
            notification.show();
        });

        Layers.Prompts.onSceneEnter.add((scene: string) => {
            if (scene === "gameintro") {
                this.setRTP();
            }
        });

        const setBetText = (layer: Layers) => {
            let betText = layer.getFlexiText("bet_text");
            if (betText) {
                let betOptions = betModel.generateBetOptions();
                let min = betOptions.coinSizes[0] * betOptions.stakePerLineValues[0] * betOptions.lines;
                let max = betOptions.coinSizes.slice(-1)[0] * betOptions.stakePerLineValues.slice(-1)[0] * betOptions.lines;
                let text = `${Translations.get('min_bet')} <orange>${currency.format(min)}</orange> | ${Translations.get('max_bet')} <orange>${currency.format(max)}</orange>`;
                betText.setText(text);
                betText.setVisible(true);
            }
        }

        const hideBetText = () => {
            let betText = Layers.Menus.getFlexiText("bet_text");
            if(betText) {
                betText.setText("");
                betText.setVisible(false);
            }
        }

        Layers.MenusContent.onSceneEnter.add((scene: string) => {
            if(scene == "paytable") {
                setBetText(Layers.MenusContent);
                setBetText(Layers.Menus);
            } else {
                hideBetText();
            }
        });
        Layers.PaytableContent.onSceneEnter.add((scene: string) => {
            setBetText(Layers.PaytableContent);
            RainbowStacksComponents.jackpotMeters.populatePaytable();        
        });

        Layers.BetBar.onSceneEnter.add((scene: string) => {
            if(scene === "freespins") {
                const value = Layers.BetBar.getText("freespins_x_of_y_value");
                if(value) {
                    value.filters = [ new GlowFilter({
                        color: 0x004da3,
                        outerStrength: 3,
                        distance: 3,
                        innerStrength: 0
                    })];
                }
            }
        })

        super.complete();

        const tConfig = Components.config.matrix.transition.options;
        const reelTransition = new RSSpinReelTransition(tConfig.startGap, tConfig.stopGap, tConfig.quickStopGap, tConfig.allowPartialSkips);
        Components.matrix.setTransition(reelTransition);

        Layers.MenusContent.onSceneEnter.add(() => {
            const rulesText = Layers.MenusContent.getFlexiText("rules_body");
            const rtp = SettingsModel.rtp;
            if (rulesText) {
                if (settingsModel.isJurisdiction([Jurisdiction.UK, Jurisdiction.DE, Jurisdiction.NL]) || autoplayModel.autoplayDisabled) {
                    rulesText.setText(Translations.get("pays_game_rules_jurisdiction", { version: __VERSION__, rtp_total: rtp + "" }));
                } else if(settingsModel.socialSweepstakes && settingsModel.rtpdisplay === false) {
                    rulesText.setText(Translations.get("pays_game_rules_nortp", {version: __VERSION__, rtp_total: rtp + ""}));
                } else {
                    rulesText.setText(Translations.get("rm.bod", { version: __VERSION__, rtp_total: rtp + "" }));
                }
            }
        });

        let symbolBehaviours = Components.config.matrix.symbolBehaviours;
        if (symbolBehaviours) {
            for (const symbolBehaviour of symbolBehaviours) {
                for (const symbol of Components.matrix.getAllSymbols()) {
                    const behaviour = symbolBehaviour.id;
                    const filteredSymbols = symbolBehaviour.symbols;

                    let behaviourInstance;
                    if (behaviour == "fswin") {
                        behaviourInstance = new RSSpineSymbolBehaviour(symbol);
                    } else if(behaviour == "leprechaun") {
                        behaviourInstance = new LeprechaunSymbolBehaviour(symbol);
                    } else if(behaviour == "landpot") {
                        behaviourInstance = new RSLandSpineSymbolBehaviour(symbol);
                    }

                    if(behaviourInstance) {
                        behaviourInstance.validSymbols = filteredSymbols;
                        symbol.addBehaviour(behaviourInstance);
                        
                        symbol.refresh();
                    }
                }
            }
        }

        Layers.Custom.get("Floaties").setScene("floaties").execute();

        if(!deviceInfo.isDesktop) {
            const resizeFloaties = () => {
                const floaties = Layers.Custom.get("Floaties").getParticle("floaties");
                floaties.spawnRect.width = canvasManager.stageWidth;
                floaties.spawnRect.height = canvasManager.stageHeight;
            }
            resizeFloaties();
            deviceInfo.onLayoutChange.add(() => setTimeout(resizeFloaties, 1));
        }
    }

    protected startGame() {
        RainbowStacksComponents.init();
        super.startGame();
    }

    protected showGameIntro() {
        return sequence(
            [
                () => Contract.wrap(()=>{
                    if(soundManager.getLoading()) {
                        soundManager.onLoadComplete.addOnce(()=>{                            
                            soundManager.customEvent("base_music");
                        })
                    }
                    else {                
                        soundManager.customEvent("base_music");
                    }
                    if(!deviceInfo.isDesktop) deviceInfo.onLayoutChange.add(this.setRTP);
                }),
                () => super.showGameIntro(),
                () => Contract.wrap(() => { 
                    soundManager.customEvent("base_music"); 
                    if(!deviceInfo.isDesktop) deviceInfo.onLayoutChange.remove(this.setRTP);
                })
            ]
        );
    }

    private setRTP = () => {
        var pixiTextRtp: PIXI.Text;
        const textRtp = Translations.get('int.rtp', { rtp: SettingsModel.rtp});
        
        pixiTextRtp = Layers.Prompts.getText("rtp");

        if(pixiTextRtp) {
            pixiTextRtp.text = textRtp +'%';
            if(settingsModel.socialSweepstakes && settingsModel.rtpdisplay === false) {
                pixiTextRtp.visible = false;
            }
        }
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/states/rs-init.ts